import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryWithErrorHandler } from './useQueryWithErrorHandler';
import { refetchQueries } from './refetchQueries';
import {
  IResponseData,
  IError,
  IApiParams,
  IHistoryRecord,
  IRelatedEntitiesPayload,
  ICreateUpdateUserPayload,
  IDeleteUserFilterPayload,
  IGetTicketProducts,
  ICreateReuseConcretePayload,
  IGetTicketProductsPayload,
  IRelatedEntity,
  ICreateReuseConcreteResponse,
  ICreateReuseConcreteActionPayload,
} from 'types';
import { useAuth, useError } from '../providers';
import { useAuthClient } from './useAuthClient';
import { firebaseAnalytics } from '../analytics';
import { errors } from '../config';

const batchHistoryListQueryKey = 'getPourEvents';
const getLatestUserFilterQueryKey = 'getLatestUserFilter';
const getUserFilterQueryKey = 'getUserFilter';
const getUserFiltersQueryKey = 'getUserFilters';
const getReturnedConcreteViewQueryKey = 'getReturnedConcreteView';

export function useBatchHistoryList({
  queryParameters,
  organizationId,
}: IApiParams) {
  const authClient = useAuthClient();
  const { openModal } = useError();
  const { supplierId } = useAuth();
  const queryClient = useQueryClient();
  const [ticketProducts, setTicketProducts] = useState<IGetTicketProducts[]>(
    [],
  );
  const [hasMoreTicketProducts, setHasMoreTicketProducts] = useState(true);

  const currentSupplierId = useMemo(() => {
    return organizationId || supplierId;
  }, [organizationId, supplierId]);

  const getBatchHistoryListUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/BatchHistoryList/get-filtered-tickets`;
  }, [currentSupplierId]);

  const getTicketHistoryUrl = useMemo(() => {
    return `/api/v1/${currentSupplierId}/BatchHistoryList/get-ticket-history`;
  }, [currentSupplierId]);

  const createUpdateUserFilter = useMemo(() => {
    return `/api/v1/${currentSupplierId}/BatchHistoryList/create-update-user-filter`;
  }, [currentSupplierId]);

  const getLatestUserFilter = useMemo(() => {
    return `/api/v1/${currentSupplierId}/BatchHistoryList/get-latest-user-filter`;
  }, [currentSupplierId]);

  const getReturnedConcreteView = useMemo(() => {
    return `/api/v1/${currentSupplierId}/BatchHistoryList/get-returned-concrete-view`;
  }, [currentSupplierId]);

  const getUserFilter = useMemo(() => {
    return `/api/v1/${currentSupplierId}/BatchHistoryList/get-user-filter`;
  }, [currentSupplierId]);

  const getUserFilters = useMemo(() => {
    return `/api/v1/${currentSupplierId}/BatchHistoryList/get-user-filters`;
  }, [currentSupplierId]);

  const deleteUserFilterURL = useMemo(() => {
    return `/api/v1/${currentSupplierId}/BatchHistoryList/delete-user-filter`;
  }, [currentSupplierId]);

  const getTicketProducts = useMemo(() => {
    return `/api/v1/${currentSupplierId}/BatchHistoryList/get-ticket-products`;
  }, [currentSupplierId]);

  const createReuseConcrete = useMemo(() => {
    return `/api/v1/${currentSupplierId}/BatchHistoryList/reuse-concrete-initial`;
  }, [currentSupplierId]);

  const createReuseConcreteAction = useMemo(() => {
    return `/api/v1/${currentSupplierId}/BatchHistoryList/reuse-concrete-action`;
  }, [currentSupplierId]);

  const url = useMemo(() => {
    return queryParameters &&
      !Object.keys(queryParameters).includes('ticketNumber')
      ? getBatchHistoryListUrl
      : getTicketHistoryUrl;
  }, [getBatchHistoryListUrl, getTicketHistoryUrl, queryParameters]);

  const {
    data: latestUserFilter,
    refetch: fetchLatestUserFilter,
    isFetching: isFetchingLatestUserFilter,
    error: latestUserFilterError,
  } = useQueryWithErrorHandler<IResponseData<ICreateUpdateUserPayload>, IError>(
    {
      queryKey: [getLatestUserFilterQueryKey],

      queryFn: async () => {
        return await authClient(getLatestUserFilter, {
          queryParameters,
          method: 'GET',
        });
      },

      enabled: false,
      refetchOnWindowFocus: false,

      onError: error => {
        openModal(error);
      },
    },
  );

  const {
    data: returnedConcreteView,
    refetch: fetchReturnedConcreteView,
    isFetching: isFetchingReturnedConcreteView,
    error: returnedConcreteViewError,
  } = useQueryWithErrorHandler<IResponseData<ICreateUpdateUserPayload>, IError>(
    {
      queryKey: [getReturnedConcreteViewQueryKey],

      queryFn: async () => {
        return await authClient(getReturnedConcreteView, {
          queryParameters,
          method: 'GET',
        });
      },

      enabled: false,
      refetchOnWindowFocus: false,

      onError: error => {
        openModal(error);
      },
    },
  );

  const {
    data: userFilter,
    refetch: fetchUserFilter,
    isFetching: ifFetchingUserFilter,
    error: userFilterError,
  } = useQueryWithErrorHandler<IResponseData<ICreateUpdateUserPayload>, IError>(
    {
      queryKey: [getUserFilterQueryKey],

      queryFn: async () => {
        return await authClient(getUserFilter, {
          queryParameters,
          method: 'GET',
        });
      },

      enabled: false,
      refetchOnWindowFocus: false,
      gcTime: 0,

      onError: error => {
        openModal(error);
      },
    },
  );

  const {
    mutateAsync: fetchTicketProducts,
    isPending: isFetchingTicketProducts,
    error: ticketProductsError,
  } = useMutation({
    mutationFn: async (payload: IGetTicketProductsPayload) => {
      return authClient(getTicketProducts, {
        queryParameters: {
          ...payload,
          skip: payload.page * payload.take,
        },
        method: 'GET',
      }).then(res => {
        setTicketProducts(prevState => {
          if (!payload.page || (res.data.length && !prevState)) {
            return res.data;
          } else {
            return prevState.concat(res.data);
          }
        });

        if (res.data.length < payload.take) {
          setHasMoreTicketProducts(false);
        }
      });
    },

    retry: false,

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const {
    data: userFilters,
    refetch: fetchUserFilters,
    isFetching: ifFetchingUserFilters,
    error: userFilterErrors,
  } = useQueryWithErrorHandler<
    IResponseData<ICreateUpdateUserPayload[]>,
    IError
  >({
    queryKey: [getUserFiltersQueryKey],

    queryFn: async () => {
      return await authClient(getUserFilters, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      openModal(error);
    },
  });

  const {
    data: createUpdateUserFilterResponse,
    mutateAsync: createUpdateUserFilterPost,
    isPending: isLoadingCreateUpdateUserFilter,
  } = useMutation({
    mutationFn: (payload: ICreateUpdateUserPayload) =>
      authClient(createUpdateUserFilter, {
        method: 'POST',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [getUserFiltersQueryKey],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        getUserFiltersQueryKey,
      ]);
    },

    onError: (error: IError) => {
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [getUserFiltersQueryKey],
      });
      refetchQueries(queryClient, {
        queryKey: [getUserFiltersQueryKey],
      });
    },
  });

  const {
    data: createReuseConcreteActionResponse,
    mutateAsync: createReuseConcreteActionPost,
    isPending: isLoadingCreateReuseConcreteAction,
  } = useMutation({
    mutationFn: (
      payload: ICreateReuseConcreteActionPayload,
    ): Promise<IResponseData<ICreateReuseConcreteResponse>> =>
      authClient(createReuseConcreteAction, {
        method: 'POST',
        data: {
          returnConcreteOrderDataId: payload.returnConcreteOrderDataId,
          partitionDateUtc: payload.partitionDateUtc,
          reuseAction: payload.reuseAction,
        },
      }),

    retry: false,

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const {
    data: createReuseConcreteResponse,
    mutateAsync: createReuseConcretePost,
    isPending: isLoadingCreateReuseConcrete,
  } = useMutation({
    mutationFn: (
      payload: ICreateReuseConcretePayload,
    ): Promise<IResponseData<ICreateReuseConcreteResponse>> =>
      authClient(createReuseConcrete, {
        method: 'POST',
        data: {
          returnConcreteOrderDataId: payload.returnConcreteOrderDataId,
          partitionDateUtc: payload.partitionDateUtc,
        },
      }),

    retry: false,

    onError: (error: IError) => {
      openModal(error);
    },
  });

  const {
    data: deleteUserFilterRes,
    mutateAsync: deleteUserFilter,
    isPending: isDeletingUserFilter,
  } = useMutation({
    mutationFn: (payload: IDeleteUserFilterPayload) =>
      authClient(deleteUserFilterURL, {
        method: 'DELETE',
        //@ts-ignore
        queryParameters: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [getUserFiltersQueryKey],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        getUserFiltersQueryKey,
      ]);
    },

    onError: (error: IError) => {
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [getUserFiltersQueryKey],
      });
      refetchQueries(queryClient, {
        queryKey: [getUserFiltersQueryKey],
      });
    },
  });

  const {
    data: batchesHistoryResponse,
    mutateAsync: fetchBatchesHistory,
    isPending: isFetchingBatchesHistory,
  } = useMutation({
    mutationFn: (payload: IRelatedEntitiesPayload) =>
      authClient(getBatchHistoryListUrl, {
        method: 'POST',
        data: payload,
      }),

    retry: false,

    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [batchHistoryListQueryKey],
      });
      return queryClient.getQueryData<IResponseData<any>>([
        batchHistoryListQueryKey,
      ]);
    },

    onError: (error: IError, variables, context) => {
      if (context) {
        queryClient.setQueryData<IResponseData<any>>(
          [batchHistoryListQueryKey],
          context,
        );
      }

      firebaseAnalytics.logErrorEvent(errors.getBatchHistoryList, error, {
        batchHistoryListQueryKey,
        variables: JSON.stringify(variables),
      });
      openModal(error);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [batchHistoryListQueryKey],
      });
    },
  });

  const {
    data: ticketHistoryResponse,
    refetch: fetchTicketHistory,
    isFetching: isFetchingTicketHistory,
    error: fetchTicketHistoryError,
  } = useQueryWithErrorHandler<IResponseData<IHistoryRecord[]>, IError>({
    queryKey: [batchHistoryListQueryKey, { queryParameters }],

    queryFn: async () => {
      return await authClient(getTicketHistoryUrl, {
        queryParameters,
        method: 'GET',
      });
    },

    enabled: false,
    refetchOnWindowFocus: false,

    onError: error => {
      firebaseAnalytics.logErrorEvent(errors.getPourEventHistory, error, {
        url,
        ...queryParameters,
      });
      openModal(error);
    },
  });

  const batchesHistory = useMemo(() => {
    return queryParameters?.ticketNumber
      ? ticketHistoryResponse?.data
      : batchesHistoryResponse?.data;
  }, [
    batchesHistoryResponse?.data,
    queryParameters?.ticketNumber,
    ticketHistoryResponse?.data,
  ]);

  const batchesHistoryMetadata = useMemo(() => {
    return queryParameters?.ticketNumber
      ? ticketHistoryResponse?.metadata
      : batchesHistoryResponse?.metadata;
  }, [
    batchesHistoryResponse?.metadata,
    queryParameters?.ticketNumber,
    ticketHistoryResponse?.metadata,
  ]);

  return {
    fetchTicketHistory,
    isFetchingTicketHistory,
    fetchTicketHistoryError,
    batchesHistory,
    fetchBatchesHistory,
    isFetchingBatchesHistory,
    batchesHistoryMetadata,
    createUpdateUserFilterResponse,
    isLoadingCreateUpdateUserFilter,
    createUpdateUserFilterPost,
    latestUserFilter,
    fetchLatestUserFilter,
    isFetchingLatestUserFilter,
    latestUserFilterError,
    userFilter,
    fetchUserFilter,
    ifFetchingUserFilter,
    userFilterError,
    userFilters,
    fetchUserFilters,
    ifFetchingUserFilters,
    userFilterErrors,
    deleteUserFilterRes,
    deleteUserFilter,
    isDeletingUserFilter,
    ticketProducts,
    fetchTicketProducts,
    isFetchingTicketProducts,
    ticketProductsError,
    createReuseConcreteResponse,
    isLoadingCreateReuseConcrete,
    createReuseConcretePost,
    hasMoreTicketProducts,
    returnedConcreteView,
    fetchReturnedConcreteView,
    isFetchingReturnedConcreteView,
    returnedConcreteViewError,
    createReuseConcreteActionResponse,
    isLoadingCreateReuseConcreteAction,
    createReuseConcreteActionPost,
  };
}
